import React from 'react'
import { Button, Drawer, Modal, Tooltip } from 'antd'
import {
  CheckCircleFilled,
  ClockCircleFilled,
  HeartOutlined,
  HomeOutlined,
  QuestionCircleOutlined,
  RightOutlined,
  ShopOutlined
} from '@ant-design/icons'
import classNames from 'classnames'
import { MdPreview } from 'md-editor-rt'
import { ICharacteristic, IPreviewData } from 'models/product'
import { useIsMobile } from 'shared/hooks/useIsMobile'
import { getNormalizedPrice } from 'shared/utils/getNormalizedPrice'
import { getCharacteristicValue } from '../ProductForm/utils/variationAxes'
import styles from './ProductCardPreview.module.scss'
import { demoteMarkdownHeadings } from './utils/demoteMarkdownHeadings'
import { getPreviewAxes } from './utils/getPreviewAxes'

interface IProductCardPreviewProps {
  open: boolean
  data: IPreviewData | null
  article?: string
  sellerName?: string
  // Цена до скидки: на сайте показывается зачёркнутой рядом с price
  fullPrice?: number | null
  onDemand?: boolean
  isDisabled?: boolean
  close: () => void
}

const Characteristic: React.FC<{ item: ICharacteristic }> = ({ item }) => (
  <div className={styles.characteristic}>
    <dt className={styles.characteristicName}>{item.name}:</dt>
    <dd className={styles.characteristicValue}>{item.value}</dd>
  </div>
)

export const ProductCardPreview: React.FC<IProductCardPreviewProps> = ({
  open,
  data,
  article,
  sellerName,
  fullPrice,
  onDemand,
  isDisabled,
  close
}) => {
  const [activePhoto, setActivePhoto] = React.useState(0)
  const isMobile = useIsMobile()

  React.useEffect(() => {
    if (open) {
      setActivePhoto(0)
    }
  }, [open])

  const photos: string[] = React.useMemo(
    () =>
      (data?.photo ?? [])
        .map((item: { thumbUrl?: string; url?: string }) => item?.thumbUrl || item?.url)
        .filter(Boolean),
    [data?.photo]
  )

  const axes = React.useMemo(
    () => getPreviewAxes(data?.variations ?? [], data?.characteristics ?? []),
    [data?.variations, data?.characteristics]
  )

  const characteristics = data?.characteristics ?? []
  const inStock = Number(data?.dimension?.in_stock) || 0
  const isUnavailable = isDisabled || (!onDemand && inStock <= 0)
  const hasDiscount = !!fullPrice && !!data?.price && fullPrice > data.price

  const disclaimer = (
    <p className={styles.disclaimer}>
      Информация о технических характеристиках, комплекте поставки, стране
      изготовления, внешнем виде и цвете товара носит справочный характер и
      основывается на последних доступных к моменту публикации сведениях
    </p>
  )

  const title = (
    <span className={styles.title}>
      Просмотр карточки
      <Tooltip title='Карточка собрана из текущих полей формы, включая несохранённые правки. Отзывы и сроки доставки здесь не показываются.'>
        <QuestionCircleOutlined className={styles.hint} />
      </Tooltip>
    </span>
  )

  const crumbs = [data?.categoryName, data?.subCategoryName, data?.name].filter(Boolean)

  const content = data && (
    <div className={classNames(styles.page, { [styles.pageMobile]: isMobile })}>
      {isMobile ? (
        <nav className={classNames(styles.block, styles.breadcrumbs)}>
          <HomeOutlined className={styles.breadcrumbsHome} />
          {crumbs.map((crumb, index) => (
            <React.Fragment key={index}>
              {index > 0 && <RightOutlined className={styles.breadcrumbsSeparator} />}
              <span>{crumb}</span>
            </React.Fragment>
          ))}
        </nav>
      ) : (
        !!data.categoryName && (
          <div className={styles.breadcrumbs}>
            Главная / {data.categoryName}
            {!!data.subCategoryName && ` / ${data.subCategoryName}`}
          </div>
        )
      )}
      <div className={styles.top}>
        <section className={classNames(styles.block, styles.about)}>
          <h1 className={styles.name}>
            {data.name}
            {!!article && ` (арт. ${article})`}
          </h1>
          <div className={styles.aboutContent}>
            <div className={styles.gallery}>
              <div className={styles.mainPhoto}>
                {photos[activePhoto] ? (
                  <img src={photos[activePhoto]} alt={data.name} />
                ) : (
                  <span className={styles.noPhoto}>Нет фото</span>
                )}
              </div>
              {photos.length > 1 && (
                <div className={styles.thumbs}>
                  {photos.map((src, index) => (
                    <button
                      key={src}
                      type='button'
                      className={classNames(styles.thumb, {
                        [styles.thumbActive]: index === activePhoto
                      })}
                      onClick={() => setActivePhoto(index)}
                    >
                      <img src={src} alt='' />
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className={styles.summary}>
              {!!axes.length && (
                <>
                  <h2 className={styles.sectionTitle}>Параметры:</h2>
                  {axes.map((axis) => (
                    <div key={axis.name} className={styles.axis}>
                      <div className={styles.axisName}>{axis.name}</div>
                      <div className={styles.chips}>
                        {axis.values.map((value) => (
                          <span
                            key={value}
                            className={classNames(styles.chip, {
                              [styles.chipActive]:
                                value ===
                                getCharacteristicValue(characteristics, axis.name)
                            })}
                          >
                            {value}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </>
              )}
              <h2
                className={styles.sectionTitle}
                style={{ marginTop: axes.length ? 18 : 0 }}
              >
                Характеристики:
              </h2>
              <dl>
                {characteristics.slice(0, 5).map((item, index) => (
                  <Characteristic key={index} item={item} />
                ))}
                {!!article && (
                  <Characteristic item={{ name: 'Артикул', value: article }} />
                )}
              </dl>
            </div>
          </div>
        </section>
        <section className={classNames(styles.block, styles.priceBlock)}>
          {!!sellerName && (
            <div className={styles.seller}>
              {isMobile && (
                <span className={styles.sellerIcon}>
                  <ShopOutlined />
                </span>
              )}
              <span className={styles.sellerName}>{sellerName}</span>
              {isMobile && <span className={styles.contactSeller}>Написать продавцу</span>}
            </div>
          )}
          <div className={styles.priceRow}>
            <span className={styles.price}>
              {getNormalizedPrice(data.price || 0)}
            </span>
            {hasDiscount && (
              <span className={styles.fullPrice}>
                {getNormalizedPrice(fullPrice as number)}
              </span>
            )}
          </div>
          {!isUnavailable && (
            <div className={styles.stock}>
              {isMobile &&
                (onDemand ? (
                  <ClockCircleFilled className={styles.onDemandIcon} />
                ) : (
                  <CheckCircleFilled className={styles.inStockIcon} />
                ))}
              {onDemand ? 'Под заказ' : 'В наличии'}
            </div>
          )}
          <div className={styles.actions}>
            <div
              className={classNames(styles.cartButton, {
                [styles.cartButtonMuted]: isUnavailable
              })}
            >
              {isUnavailable ? 'Нет в наличии' : 'Добавить в корзину'}
            </div>
            {isMobile && (
              <span className={styles.favoriteButton}>
                <HeartOutlined />
              </span>
            )}
          </div>
        </section>
      </div>
      <section className={classNames(styles.block, styles.description)}>
        <h2 className={styles.descriptionTitle}>Описание товара:</h2>
        <MdPreview
          id='product-card-preview'
          language='ru'
          value={demoteMarkdownHeadings(data.desc)}
          style={{ fontFamily: 'inherit' }}
        />
        {!characteristics.length && disclaimer}
      </section>
      {!!characteristics.length && (
        <section className={classNames(styles.block, styles.description)}>
          <h2 className={styles.descriptionTitle}>Характеристики:</h2>
          <dl className={styles.characteristicsGrid}>
            {characteristics.map((item, index) => (
              <Characteristic key={index} item={item} />
            ))}
          </dl>
          {disclaimer}
        </section>
      )}
    </div>
  )

  if (isMobile) {
    return (
      <Drawer
        open={open}
        onClose={close}
        placement='bottom'
        height='100%'
        title={title}
        rootClassName={styles.drawer}
        destroyOnClose
      >
        {content}
      </Drawer>
    )
  }

  return (
    <Modal
      open={open}
      onCancel={close}
      width={1100}
      style={{ top: 20 }}
      title={title}
      footer={
        <Button size='large' onClick={close}>
          Закрыть
        </Button>
      }
      destroyOnClose
    >
      {content}
    </Modal>
  )
}
